import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail, Megaphone, FileText, Target, ShoppingBag, Mic, Send, BookmarkCheck,
  Copy, Eye, Search, Plus, Pencil, Trash2, Tag, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { EmptyState as SharedEmptyState } from "@/components/empty-state";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

import {
  listSwipeCategories, listSwipes, getSwipe,
  upsertSwipe, deleteSwipe, incrementSwipeStat,
  canEditSwipeCategory,
} from "@/lib/swipes.functions";
import { listMyAccounts } from "@/lib/workspaces.functions";

export const Route = createFileRoute("/_authenticated/swipe/")({
  component: SwipeLibraryPage,
});

const ICONS: Record<string, any> = {
  email: Mail, social: Megaphone, ad: Target, sales_copy: ShoppingBag,
  script: Mic, document: FileText, other: Send,
};

const TYPE_LABEL: Record<string, string> = {
  email: "Email", social: "Social", ad: "Ad", sales_copy: "Sales Copy",
  script: "Script", document: "Document", other: "Other",
};

function SwipeLibraryPage() {
  const fetchCats = useServerFn(listSwipeCategories);
  const fetchSwipes = useServerFn(listSwipes);
  const fetchAccts = useServerFn(listMyAccounts);

  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [libraryTab, setLibraryTab] = useState<"all" | "foundation" | "account">("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: catsData } = useQuery({ queryKey: ["swipe-categories"], queryFn: () => fetchCats() });
  const { data: acctData } = useQuery({ queryKey: ["my-accounts"], queryFn: () => fetchAccts() });
  const primaryAccount = acctData?.accounts?.[0];

  const { data: swipesData, isLoading } = useQuery({
    queryKey: ["swipes", activeCat, libraryTab, search, typeFilter],
    queryFn: () => fetchSwipes({
      data: {
        category_id: activeCat ?? undefined,
        library_type: libraryTab,
        search: search.trim() || undefined,
        swipe_type: typeFilter === "all" ? undefined : (typeFilter as any),
      } as any,
    }),
  });

  const categories = catsData?.categories ?? [];
  const swipes = swipesData?.swipes ?? [];

  const swipeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of swipes) counts[s.category_id] = (counts[s.category_id] ?? 0) + 1;
    return counts;
  }, [swipes]);

  return (
    <div className="flex h-[calc(100vh-3rem)] w-full">
      {/* Categories sidebar */}
      <aside className="w-64 shrink-0 border-r bg-muted/20 flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-1">
            <BookmarkCheck className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Swipe Files</h2>
          </div>
          <p className="text-xs text-muted-foreground">Winning copy, ready to remix</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-0.5">
            <CategoryButton
              active={activeCat === null}
              onClick={() => setActiveCat(null)}
              label="All swipes"
              count={swipes.length}
              icon={<BookmarkCheck className="h-4 w-4" />}
            />
            <Separator className="my-2" />
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Foundation
            </div>
            {categories.filter((c: any) => c.library_type === "foundation").map((c: any) => {
              const Icon = ICONS[c.icon ?? "other"] ?? FileText;
              return (
                <CategoryButton
                  key={c.id}
                  active={activeCat === c.id}
                  onClick={() => setActiveCat(c.id)}
                  label={c.name}
                  count={swipeCounts[c.id]}
                  icon={<Icon className="h-4 w-4" />}
                />
              );
            })}
            {categories.some((c: any) => c.library_type === "account") && (
              <>
                <Separator className="my-2" />
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Your library
                </div>
                {categories.filter((c: any) => c.library_type === "account").map((c: any) => {
                  const Icon = ICONS[c.icon ?? "other"] ?? FileText;
                  return (
                    <CategoryButton
                      key={c.id}
                      active={activeCat === c.id}
                      onClick={() => setActiveCat(c.id)}
                      label={c.name}
                      count={swipeCounts[c.id]}
                      icon={<Icon className="h-4 w-4" />}
                    />
                  );
                })}
              </>
            )}
          </div>
        </ScrollArea>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold tracking-tight">
                {activeCat
                  ? categories.find((c: any) => c.id === activeCat)?.name
                  : "All Swipes"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {swipes.length} swipe{swipes.length === 1 ? "" : "s"}
              </p>
            </div>
            <Button
              onClick={() => { setEditingId(null); setEditorOpen(true); }}
              disabled={!primaryAccount}
            >
              <Plus className="h-4 w-4 mr-1" /> New swipe
            </Button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, body, use case…"
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(TYPE_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={libraryTab} onValueChange={(v) => setLibraryTab(v as any)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="foundation">Foundation</TabsTrigger>
                <TabsTrigger value="account">Mine</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : swipes.length === 0 ? (
              <EmptyState onCreate={() => { setEditingId(null); setEditorOpen(true); }} canCreate={!!primaryAccount} />
            ) : (
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                {swipes.map((s: any) => (
                  <SwipeCard key={s.id} swipe={s} onOpen={() => setOpenSwipeId(s.id)} />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </main>

      <SwipeDetailDialog
        swipeId={openSwipeId}
        onClose={() => setOpenSwipeId(null)}
        onEdit={(id) => { setOpenSwipeId(null); setEditingId(id); setEditorOpen(true); }}
      />

      <SwipeEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        swipeId={editingId}
        categories={categories}
        defaultCategoryId={activeCat ?? categories[0]?.id ?? null}
        accountId={primaryAccount?.id ?? null}
      />
    </div>
  );
}

// ---------- Sub-components ----------

function CategoryButton({
  active, onClick, label, count, icon,
}: { active: boolean; onClick: () => void; label: string; count?: number; icon: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 text-left truncate">{label}</span>
      {count != null && count > 0 && (
        <span className={`text-xs ${active ? "opacity-80" : "text-muted-foreground"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function SwipeCard({ swipe, onOpen }: { swipe: any; onOpen: () => void }) {
  const Icon = ICONS[swipe.swipe_type] ?? FileText;
  const preview = (swipe.body_content ?? "").slice(0, 220);
  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors flex flex-col"
      onClick={onOpen}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-primary shrink-0" />
            <CardTitle className="text-sm font-semibold truncate">{swipe.title}</CardTitle>
          </div>
          {swipe.account_id === null && (
            <Badge variant="secondary" className="text-[10px] shrink-0">Foundation</Badge>
          )}
        </div>
        {swipe.subject_line && (
          <CardDescription className="text-xs truncate">"{swipe.subject_line}"</CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-2">
        <p className="text-xs text-muted-foreground line-clamp-4 whitespace-pre-wrap font-mono">
          {preview || swipe.use_case || "No preview"}
        </p>
        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex gap-1 flex-wrap">
            {(swipe.tags ?? []).slice(0, 3).map((t: string) => (
              <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{swipe.view_count}</span>
            <span className="flex items-center gap-1"><Copy className="h-3 w-3" />{swipe.copy_count}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ onCreate, canCreate }: { onCreate: () => void; canCreate: boolean }) {
  return (
    <SharedEmptyState
      icon={Sparkles}
      title="No swipes here yet"
      description="Try a different category or filter, or save your first proven asset to this library."
      action={canCreate ? (
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4 mr-1" /> New swipe
        </Button>
      ) : undefined}
    />
  );
}


function SwipeDetailDialog({
  swipeId, onClose, onEdit,
}: { swipeId: string | null; onClose: () => void; onEdit: (id: string) => void }) {
  const getOne = useServerFn(getSwipe);
  const incStat = useServerFn(incrementSwipeStat);
  const canEditFn = useServerFn(canEditSwipeCategory);
  const removeFn = useServerFn(deleteSwipe);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["swipe", swipeId],
    queryFn: () => getOne({ data: { id: swipeId! } }),
    enabled: !!swipeId,
  });
  const s = data?.swipe;

  const { data: editPerm } = useQuery({
    queryKey: ["swipe-can-edit", s?.category_id],
    queryFn: () => canEditFn({ data: { category_id: s!.category_id } }),
    enabled: !!s?.category_id,
  });

  // Fire view once when opened
  useMemo(() => {
    if (swipeId) incStat({ data: { id: swipeId, kind: "view" } }).catch(() => {});
  }, [swipeId, incStat]);

  const removeMut = useMutation({
    mutationFn: (id: string) => removeFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Swipe deleted");
      qc.invalidateQueries({ queryKey: ["swipes"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to delete"),
  });

  const onCopy = async () => {
    if (!s) return;
    const text = [s.subject_line ? `Subject: ${s.subject_line}\n` : "", s.body_content ?? ""].join("");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
      await incStat({ data: { id: s.id, kind: "copy" } });
      qc.invalidateQueries({ queryKey: ["swipes"] });
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <Dialog open={!!swipeId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        {s && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle className="flex-1">{s.title}</DialogTitle>
                <Badge variant="outline">{TYPE_LABEL[s.swipe_type] ?? s.swipe_type}</Badge>
              </div>
              {s.use_case && <DialogDescription>{s.use_case}</DialogDescription>}
            </DialogHeader>

            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-2">
                {s.subject_line && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Subject line</Label>
                    <div className="mt-1 p-3 rounded-md bg-muted font-mono text-sm">{s.subject_line}</div>
                  </div>
                )}
                {s.body_content && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Body</Label>
                    <pre className="mt-1 p-3 rounded-md bg-muted whitespace-pre-wrap font-mono text-sm leading-relaxed">
                      {s.body_content}
                    </pre>
                  </div>
                )}
                {s.performance_notes && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Performance notes</Label>
                    <p className="mt-1 text-sm text-muted-foreground">{s.performance_notes}</p>
                  </div>
                )}
                {(s.tags ?? []).length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    {s.tags.map((t: string) => (
                      <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>

            <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
              {editPerm?.canEdit && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => removeMut.mutate(s.id)}
                    disabled={removeMut.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                  <Button variant="outline" onClick={() => onEdit(s.id)}>
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                </>
              )}
              <Button onClick={onCopy}>
                <Copy className="h-4 w-4 mr-1" /> Copy swipe
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SwipeEditorDialog({
  open, onOpenChange, swipeId, categories, defaultCategoryId, accountId,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  swipeId: string | null;
  categories: any[];
  defaultCategoryId: string | null;
  accountId: string | null;
}) {
  const getOne = useServerFn(getSwipe);
  const upsert = useServerFn(upsertSwipe);
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ["swipe-edit", swipeId],
    queryFn: () => getOne({ data: { id: swipeId! } }),
    enabled: open && !!swipeId,
  });

  const initial = existing?.swipe;
  const [form, setForm] = useState<any>({
    category_id: defaultCategoryId, title: "", swipe_type: "email",
    subject_line: "", body_content: "", use_case: "", performance_notes: "",
    tags: "", is_published: true,
  });

  useMemo(() => {
    if (open) {
      if (initial) {
        setForm({
          category_id: initial.category_id,
          title: initial.title ?? "",
          swipe_type: initial.swipe_type ?? "email",
          subject_line: initial.subject_line ?? "",
          body_content: initial.body_content ?? "",
          use_case: initial.use_case ?? "",
          performance_notes: initial.performance_notes ?? "",
          tags: (initial.tags ?? []).join(", "),
          is_published: initial.is_published,
        });
      } else if (!swipeId) {
        setForm((f: any) => ({ ...f, category_id: defaultCategoryId }));
      }
    }
  }, [open, initial, swipeId, defaultCategoryId]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form.category_id) throw new Error("Pick a category");
      if (!form.title.trim()) throw new Error("Title is required");
      const tags = form.tags.split(",").map((t: string) => t.trim()).filter(Boolean).slice(0, 20);
      const payload: any = {
        id: swipeId ?? undefined,
        category_id: form.category_id,
        account_id: accountId, // foundation edits not supported in account UI
        title: form.title.trim(),
        swipe_type: form.swipe_type,
        subject_line: form.subject_line || null,
        body_content: form.body_content || null,
        use_case: form.use_case || null,
        performance_notes: form.performance_notes || null,
        tags,
        is_published: form.is_published,
      };
      return upsert({ data: payload });
    },
    onSuccess: () => {
      toast.success(swipeId ? "Swipe updated" : "Swipe saved");
      qc.invalidateQueries({ queryKey: ["swipes"] });
      qc.invalidateQueries({ queryKey: ["swipe", swipeId] });
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to save"),
  });

  const allCats = categories;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{swipeId ? "Edit swipe" : "New swipe"}</DialogTitle>
          <DialogDescription>
            Save winning copy you want to reuse. Use {`{{first_name}}`} style placeholders for personalization.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category_id ?? undefined}
                  onValueChange={(v) => setForm({ ...form, category_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>
                    {allCats.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.library_type === "foundation" ? "★ " : ""}{c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.swipe_type} onValueChange={(v) => setForm({ ...form, swipe_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Last-chance reminder email" />
            </div>

            {(form.swipe_type === "email") && (
              <div>
                <Label>Subject line</Label>
                <Input value={form.subject_line} onChange={(e) => setForm({ ...form, subject_line: e.target.value })} />
              </div>
            )}

            <div>
              <Label>Body / copy</Label>
              <Textarea
                rows={10}
                value={form.body_content}
                onChange={(e) => setForm({ ...form, body_content: e.target.value })}
                className="font-mono text-sm"
                placeholder="Paste your proven copy here…"
              />
            </div>

            <div>
              <Label>Use case</Label>
              <Input value={form.use_case} onChange={(e) => setForm({ ...form, use_case: e.target.value })} placeholder="When to use this swipe" />
            </div>

            <div>
              <Label>Performance notes</Label>
              <Textarea
                rows={2}
                value={form.performance_notes}
                onChange={(e) => setForm({ ...form, performance_notes: e.target.value })}
                placeholder="Open rates, CTR, what worked, what didn't…"
              />
            </div>

            <div>
              <Label>Tags (comma separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="urgency, last-chance, launch" />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save swipe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
